/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `VirtualEventCard` for the Caintra Connect frontend.
 * @module components/Events/EventListItem/VirtualEventCard
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import dayjs from "dayjs";
import { getVirtualEventResolvedPrice } from "../../../logic/virtualEvents";
import { truncateText } from "../../../utils/common.js";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/Calendar_Today.svg";
import { ReactComponent as SalesIcon } from "../../../assets/icons/Sales.svg";
import {
  EventCardShell,
  EventDetailColumn,
  RegisteredStatusTag,
  SingleEventDate,
} from "./shared.jsx";

const VirtualEventCard = ({
  event,
  eventKey,
  user,
  t,
  styles,
  virtualRegisterModalOpen,
  virtualRegisterModalEvent,
  virtualRegisterError,
  onViewVirtualRegistration,
  onOpenVirtualRegisterModal,
}) => (
  <EventCardShell event={event} styles={styles} eventKey={eventKey}>
    <div className={styles.eventDetails}>
      <div className={styles.registrationEventDate}>
        <SingleEventDate eventDate={event.fechaEvento} styles={styles} />
        {event.isUserRegistered ? (
          <button
            type="button"
            className={styles.viewRegistrationButton}
            onClick={() => onViewVirtualRegistration(event)}
          >
            {t("pages.events.viewRegistration")}
          </button>
        ) : user?.tipoDeUsuario !== "12" ? (
          <button
            type="button"
            className={styles.registerButton}
            onClick={() => onOpenVirtualRegisterModal(event)}
            disabled={
              virtualRegisterModalOpen &&
              virtualRegisterModalEvent?._id === event._id
            }
          >
            {t("pages.events.register")}
          </button>
        ) : null}
      </div>
      <div className={styles.divider} />
      <div className={styles.eventDescriptionContainer}>
        <span className={styles.eventDescription}>
          {truncateText(event.descripcion, 50)}
        </span>
        {event.isUserRegistered && <RegisteredStatusTag t={t} styles={styles} />}
        {virtualRegisterError?.eventId === event._id && (
          <div className={styles.eventTags}>
            <span className={styles.eventDescription}>
              {virtualRegisterError.message}
            </span>
          </div>
        )}
      </div>
    </div>
    <div className={styles.eventAdditionalDetails}>
      {getVirtualEventResolvedPrice(event, user) > 0 && (
        <EventDetailColumn
          icon={SalesIcon}
          label={t("pages.events.cost")}
          value={`$ ${getVirtualEventResolvedPrice(event, user)} MXN`}
          styles={styles}
        />
      )}
      {event.fechaLimiteRegistro && (
        <EventDetailColumn
          icon={CalendarIcon}
          label={t("pages.events.dueDateRegistration")}
          value={dayjs(event.fechaLimiteRegistro).utc().format("LLL")}
          styles={styles}
        />
      )}
    </div>
  </EventCardShell>
);

export default VirtualEventCard;
